/**
 * @description
 * FsesComponent manages the tracking, filtering, extraction, and teletransmission of FSEs.
 * Accessible via the `/fse/fses` route.
 *
 * Dependencies:
 * - FseService (FSE business logic and API communication)
 * - MercalinkFselibService (FSE serialization and formatting)
 * - LoginService (User session and active station configuration)
 * - ServerStatusService (Heartbeat and server offline detection)
 * - AlertService & SpinnerService (Notifications and loading state)
 * - StateHelper & ValuesService (FSE state formatting and metadata)
 */

import { Component, DestroyRef, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NavigationEnd, Router, RouterModule } from '@angular/router';

import { catchError, EMPTY, forkJoin, Observable, of, Subscription, switchMap, tap, throwError, from, concatMap, toArray } from 'rxjs';
import Swal from 'sweetalert2';
import * as fselib from 'mercalink-fselib';
import { SupprimerHTTPResponse, tofseformat } from 'mercalink-fselib';

import { LoginService } from '@app/common/login.service';
import { ServerStatusService } from '@app/common/server-status.service';
import { ValuesService } from '@app/common/values/values.service';
import { DetailLotComponent } from '@app/fse/detail-lot/detail-lot.component';
import { DetailComponent } from '@app/fse/detail/detail.component';
import { DossiersScoreComponent } from '@app/fse/dossiers-score/dossiers-score.component';
import { OutputDialogData, RecipientsComponent } from '@app/fse/recipients/recipients.component';
import { AlertService } from '@app/shared/alert.service';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { OfflineBannerComponent } from '@app/shared/offline-banner/offline-banner.component';
import { FormatCurrencyPipe, IsoDateTransformPipe } from '@app/shared/pipes/format-currency.pipe';
import { SpinnerService } from '@app/shared/spinner.service';
import * as util from '@app/shared/util';
import { User } from '@app/shared/_models/common';

import { FactureSearcher, FseService, StateHelper } from '../fse.service';
import * as dao from './dao';

import { environment } from '../../../environments/environment';
@Component({
  selector: 'app-fses',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, RouterModule, MenuCommonComponent, OfflineBannerComponent, MatButtonModule, MatTableModule, MatIconModule, MatMenuModule, MatDividerModule, IsoDateTransformPipe, CommonModule, MatSelectModule, MatFormFieldModule, MatInputModule, FormatCurrencyPipe],
  templateUrl: './fses.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fses.component.scss',
})
export class FsesComponent {
  public displayedColumns_db: string[] = ['numFacture', 'dateFacture', 'flux', 'id_order', 'bs', 'fse_type', 'fse_state', 'mnt_tot', 'du_amo', 'org_amo', 'batch', 'batch_state', 'org_amc', 'batch_dre', 'batch_state_dre', 'attach', 'delete', 'print']; // , 'batch_state', 'batch'
  public displayedColumns_af: string[] = ['numFacture', 'dateFacture'];
  private fses_db: any; // FSEs en base
  private fses_af: any; // FSE non mises en lot (sécurisées)
  public dataSource_db: MatTableDataSource<AbstractControl> = new MatTableDataSource();
  public dataSource_af: MatTableDataSource<AbstractControl> = new MatTableDataSource();
  public fselib = fselib;
  private routerevents$: any;
  public util = util;
  private openbssubscription!: Subscription;
  private recipientssubscription!: Subscription;

  public searcher!: FactureSearcher;
  public user: User | undefined; // Logged-in user
  public numIdentFact: string | undefined = undefined;
  public hasFseConfig: boolean = false;
  public zoneMsg = '';
  private area_baseurl?: string = undefined;
  private configarea?: fselib.ConfigArea;
  @ViewChild('searchInput', { static: true }) searchInput: any;

  public stateList: { code: string; label: string }[] = [
    { code: 'C', label: 'En cours' },
    { code: 'P', label: 'Payé' },
    { code: 'R', label: 'Rejeté' },
    { code: 'A', label: 'Anomalie' },
  ];

  public filters: dao.Filters = {
    datdeb: '',
    datfin: '',
    numFacture: '',
    recipient: '',
    etats: [],
    orgAmo: '',
    datcpt: '',
    paiement: '',
  };

  private valuesService = inject(ValuesService);
  public loginService = inject(LoginService);
  public serverStatusService = inject(ServerStatusService);
  private stateHelper = inject(StateHelper);
  private fseService = inject(FseService);
  private spinner = inject(SpinnerService);
  private alert = inject(AlertService);
  private meklibService = inject(fselib.MercalinkFselibService);
  private apiService = inject(fselib.MercalinkAPIService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.area_baseurl = this.loginService.area_baseurl;
    if (!this.area_baseurl) {
      Swal.fire({ icon: 'error', title: 'Erreur de configuration', text: 'Erreur de configuration de la station: area_baseurl vide' });
    }

    const station = this.loginService.currentstation;
    if (station && station.data.hasOwnProperty('configarea')) {
      this.configarea = station.data.configarea;
    }
    // this.zoneMsg = `TESTRSP5802FR`;
    this.zoneMsg = `${this.configarea?.nomLogiciel ?? ''} ${this.configarea?.versionCommerciale}`;

    // Automatically reload data when the server status transitions back to online
    this.serverStatusService.isOnline$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isOnline => {
      if (isOnline) {
        this.loadFses().subscribe();
      }
    });

    // Subscribe to router events to reload on navigation
    this.routerevents$ = this.router.events.subscribe((event: any) => {
      // Real reload, not a F5 on same page
      if (event instanceof NavigationEnd) {
        this.loadFses().subscribe();
      }
    });

    // Subscribe to the user observable from FseService to get user data and numIdentFact
    this.fseService.getUserWhenReady$().subscribe({
      next: user => {
        this.user = user;
        if (this.user && this.user.data?.FSE?.PS?.numIdentFact && this.user.data?.FSE?.PS?.numSituation) {
          this.numIdentFact = this.user.data.FSE.PS.numIdentFact;
          this.hasFseConfig = true;
          // Auto-actualise once on page entry, then load FSEs (non-blocking if AreaFSE is offline)
          this.actualiser$()
            .pipe(
              catchError(err => {
                console.warn('Initial FSE actualisation skipped/failed (AreaFSE offline or error), loading local DB data:', err);
                return of(null);
              }),
              switchMap(() => this.loadFses()),
            )
            .subscribe();
        } else {
          this.hasFseConfig = false;
          this.numIdentFact = undefined;
          this.loadFses().subscribe();
        }
      },
      error: err => {
        console.warn('Erreur lors de la récupération des informations utilisateur FSE :', err);
        this.hasFseConfig = false;
        this.loadFses().subscribe();
      },
    });

    this.searchInput.nativeElement.focus();
  }

  public loadFses(): Observable<fselib.FSEOrder[]> {
    if (!this.area_baseurl) return of([]);
    const q: string = dao.buildFilter(this.filters);
    return this.fseService.getFses$(q).pipe(
      tap((fses: fselib.FSEOrder[]) => {
        fses.forEach((fo: fselib.FSEOrder) => {
          // Loop over FSEOrder to process attach_icon & more...
          fo.payedAMO = 0;
          fo.payedAMC = 0;
          (fo as any).attach_icon = this.getAttachmentIcon(fo);
          fo.remboursableAmo = util.round_currency(+(fo.data?.processingResult?.areaResponse?.HTTPResponse?.factureFR?.totalFacture.totalRemboursableAmo ?? 0)) / 100;
          fo.remboursableAmc = util.round_currency(+(fo.data?.processingResult?.areaResponse?.HTTPResponse?.factureFR?.totalFacture.totalRemboursableAmc ?? 0)) / 100;
          fo.totalRemboursable = util.round_currency(fo.remboursableAmo + fo.remboursableAmc);

          // Loop on RSP FSEs
          if (fo.lnk_rsp_fses)
            fo.lnk_rsp_fses.forEach((rsp: fselib.RSP) => {
              const amo = rsp.totalAMO != null ? Number(rsp.totalAMO) : null;
              const amc = rsp.totalAMC != null ? Number(rsp.totalAMC) : null;

              if (amo !== null) fo.payedAMO += util.round_currency(amo) / 100;
              if (amc !== null) fo.payedAMC += util.round_currency(amc) / 100;
            });

          // Loop on RSPs DRE too
          if (fo.lnk_rsp_dres)
            fo.lnk_rsp_dres.forEach((rsp: fselib.RSP) => {
              const amc = rsp.totalAMC != null ? Number(rsp.totalAMC) : null;
              if (amc !== null) fo.payedAMC += util.round_currency(amc) / 100;
            });

          const totalPayed: number = fo.payedAMO + fo.payedAMC;
          fo.duAmo = Number((fo.remboursableAmo - fo.payedAMO).toFixed(2));
          fo.duAmc = Number((fo.remboursableAmc - fo.payedAMC).toFixed(2));
          fo.totalDu = Number((fo.totalRemboursable - totalPayed).toFixed(2));

          fo.orgAmc = fo.data?.factureFR?.beneficiaire?.attestationAMC?.numAMC ?? fo.data?.factureFR?.beneficiaire?.attestationAMC?.mutnum;
          fo.etatFactureRSP = this.getEtatFactureRSP(fo); // State computation AFTER RSP integration
          fo.etatFactureRSPClass = this.stateHelper.getEtatFactureClass(fo.etatFactureRSP[0]); // State CSS class to display - pre computation
          fo.etat_facture = this.stateHelper.getEtatFacture(fo, fo.etatFactureRSP[0]); // State to display AFTER RSP integration
        });

        if (this.filters.etats && this.filters.etats.length > 0) {
          // Filter against states
          fses = fses.filter((fo: fselib.FSEOrder) => this.filters.etats.includes(fo.etatFactureRSP[0]));
        }

        this.dataSource_db = new MatTableDataSource(fses as any);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 0 && err.status !== 502 && err.status !== 503 && err.status !== 504) {
          console.error('Error loading FSEs from DB:', err);
          Swal.fire({ icon: 'error', title: 'Erreur', text: 'Erreur lors du chargement des factures: ' + (err.message || err.statusText) });
        }
        return of([]);
      }),
    );
  }

  onClickExtraireLots(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl || !this.numIdentFact) return;
    this.fseService
      .extractLots$(area_baseurl, this.numIdentFact)
      .pipe(switchMap(() => this.loadFses()))
      .subscribe({
        next: () => {
          console.log('extractLots$ process completed successfully.');
        },
        error: error => {
          console.error('Error in extractLots$ process:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || "Une erreur est survenue lors de l'extraction des lots." });
        },
      });
  }

  onClickBordereau(): void {
    // 1. Open a blank window immediately to prevent browser popup blockers
    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.write('<html><body><p>Génération du bordereau...</p></body></html>');
    }

    this.fseService.postDocPrintEvent$(environment.server_url, { numIdentFact: this.numIdentFact, datdeb: this.filters.datdeb, datfin: this.filters.datfin }).subscribe({
      next: (response: any) => {
        // Assume response structure is { data: { html: "..." }, msg: "..." }
        const htmlContent = response.data?.html;

        if (printWindow && htmlContent) {
          // 2. Replace the "Loading" content with the actual rendered HTML
          printWindow.document.open();
          printWindow.document.write(htmlContent);
          printWindow.document.close();

          // 3. Trigger the Print dialog once the document is ready
          // We use a small timeout to ensure styles/images are parsed
          setTimeout(() => {
            printWindow.focus();
            // printWindow.print();
          }, 250);
        } else {
          printWindow?.close();
          console.error('No HTML content received from server');
        }
      },
      error: error => {
        printWindow?.close();
        console.error('Error in postDocPrintEvent$:', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Une erreur est survenue lors de la génération du document.' });
      },
    });
  }

  onClickPrint(p: fselib.FSEOrder): void {
    // 1. Ouvre UN seul onglet immédiatement (geste utilisateur → non bloqué)
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write('<html><body><p>Génération des documents (duplicata + quittance)...</p></body></html>');
    }

    // 2. Récupère les deux documents en parallèle
    forkJoin({
      duplicata: this.fseService.postDuplicataPrintEvent$(environment.server_url, p.id),
      quittance: this.fseService.postQuittancePrintEvent$(environment.server_url, p.id),
    }).subscribe({
      next: (responses: { duplicata: any; quittance: any }) => {
        const dupHtml = responses.duplicata?.data?.html;
        const quitHtml = responses.quittance?.data?.html;

        if (printWindow && (dupHtml || quitHtml)) {
          // 3a. Document principal : le duplicata s'il existe, sinon la quittance
          //     (le doc principal garde son rendu natif + ses marges @page)
          printWindow.document.open();
          printWindow.document.write(dupHtml ?? quitHtml);
          printWindow.document.close();

          // 3b. Seulement si on a LES DEUX : la quittance va dans un <iframe> ISOLÉ sous le duplicata
          if (dupHtml && quitHtml) {
            const iframe = printWindow.document.createElement('iframe');
            iframe.style.cssText = 'display:block; width:100%; border:0; page-break-before:always;';
            printWindow.document.body.appendChild(iframe);

            const idoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (idoc) {
              idoc.open();
              idoc.write(quitHtml);
              idoc.close();
              // Le duplicata impose @page margin:0 sur tout le tirage → on redonne une marge à la quittance
              if (idoc.body) idoc.body.style.margin = '15mm';
              // Ajuste la hauteur de l'iframe au contenu (documentElement pour inclure la marge)
              const resize = () => {
                iframe.style.height = Math.max(idoc.body?.scrollHeight ?? 0, idoc.documentElement?.scrollHeight ?? 0) + 'px';
              };
              iframe.onload = resize;
              setTimeout(resize, 300);
            }
          }

          setTimeout(() => printWindow.focus(), 350);
        } else {
          printWindow?.close();
          console.error('No HTML content received from server');
        }
      },
      error: (error: any) => {
        printWindow?.close();
        console.error('Error in onClickPrint:', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Une erreur est survenue lors de la génération des documents.' });
      },
    });
  }

  onClickExtraireRSPs(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl || !this.numIdentFact) return;
    this.fseService
      .extractRSPs$(area_baseurl, this.numIdentFact)
      .pipe(
        switchMap(() => {
          console.log('extractRSPs$ process completed successfully. Triggering lettering...');
          return this.fseService.postFseRspLetteringEvent$(environment.server_url);
        }),
        switchMap(() => this.loadFses()),
      )
      .subscribe({
        next: () => {
          console.log('RSP extraction, lettering and reload process completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in RSP extraction or lettering process:', error);
          Swal.fire({
            title: 'Erreur',
            text: 'Une erreur est survenue: ' + error.message,
            icon: 'error',
            confirmButtonText: 'OK',
          });
        },
      });
  }

  onClickExtraireSCOR(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl || !this.numIdentFact) return;
    this.fseService
      .extractSCOR$(area_baseurl, this.numIdentFact)
      .pipe(
        switchMap((extractSCORE: any) => {
          const failures = extractSCORE?.responses?.filter((r: any) => r && r.success === false) || [];
          if (failures.length > 0) {
            const msgs = failures.map((f: any) => f.msg || f.error_msg).join('\n');
            Swal.fire({
              icon: 'warning',
              title: 'Extraction SCOR avec avertissements',
              text: `Certaines pièces justificatives n'ont pas pu être réconciliées :\n${msgs}`,
            });
          }
          return this.loadFses();
        }),
      )
      .subscribe({
        next: () => {
          console.log('ExtraireSCOR process completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in ExtraireSCOR process:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || "Une erreur est survenue lors de l'extraction SCOR." });
        },
      });
  }

  onClickMettreEnLot(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl || !this.configarea) return;
    this.fseService
      .mettreEnLot$(area_baseurl, this.configarea)
      .pipe(switchMap(() => this.loadFses()))
      .subscribe({
        next: () => {
          console.log('mettreEnLot process completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in mettreEnLot process:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || 'Une erreur est survenue lors de la mise en lot.' });
        },
      });
  }

  onClickMettreEnFichier(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    this.fseService
      .mettreEnFichier$(area_baseurl, this.zoneMsg)
      .pipe(switchMap(() => this.loadFses()))
      .subscribe({
        next: () => {
          console.log('mettreEnFichier process completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in mettreEnFichier:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || 'Une erreur est survenue lors de la mise en fichier.' });
        },
      });
  }

  /**
   * Returns an Observable running the complete FSE refresh pipeline:
   * 1. Extraire Lots (ARL & status)
   * 2. Extraire RSPs (Retours de paiement Noémie) & Lettrage automatique
   * 3. Extraire SCOR (Accusés de réception pièces justificatives)
   */
  public actualiser$(): Observable<any> {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return throwError(() => new Error('area_baseurl vide'));
    if (!this.numIdentFact) return throwError(() => new Error('Identifiant de facturation (numIdentFact) manquant'));
    const numIdentFact = this.numIdentFact;
    return this.fseService.extractLots$(area_baseurl, numIdentFact).pipe(
      switchMap(() => this.fseService.extractRSPs$(area_baseurl, numIdentFact)),
      switchMap(() => this.fseService.postFseRspLetteringEvent$(environment.server_url)),
      switchMap(() => this.fseService.extractSCOR$(area_baseurl, numIdentFact)),
    );
  }

  /**
   * Executes the full refresh sequence (Lots -> RSPs -> SCOR) and reloads FSE data in place.
   */
  onClickActualiser(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    this.actualiser$()
      .pipe(
        switchMap((extractSCORE: any) => {
          const failures = extractSCORE?.responses?.filter((r: any) => r && r.success === false) || [];
          if (failures.length > 0) {
            const msgs = failures.map((f: any) => f.msg || f.error_msg).join('\n');
            Swal.fire({
              icon: 'warning',
              title: 'Actualisation terminée avec avertissements',
              text: `Actualisation effectuée, mais certaines pièces SCOR n'ont pas été trouvées :\n${msgs}`,
            });
          }
          return this.loadFses();
        }),
      )
      .subscribe({
        next: () => {
          console.log('Actualiser process completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in Actualiser process:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || "Une erreur est survenue lors de l'actualisation." });
        },
      });
  }

  /**
   * Executes the complete teletransmission pipeline:
   * 1. Mettre en Lots
   * 2. Mettre en Fichiers
   * 3. Télétransmettre
   * 4. Actualiser (Lots -> RSPs -> SCOR)
   * 5. Reload table in place
   */
  onClickTeletransmettre(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl || !this.configarea) return;
    this.fseService
      .mettreEnLot$(area_baseurl, this.configarea)
      .pipe(
        switchMap(() => this.fseService.mettreEnFichier$(area_baseurl, this.zoneMsg)),
        switchMap(() => this.fseService.teletransmettre$(area_baseurl)),
        switchMap(() => this.actualiser$()),
        switchMap(() => this.loadFses()),
      )
      .subscribe({
        next: () => {
          console.log('Full teletransmission and refresh pipeline completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in teletransmission pipeline:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || 'Une erreur est survenue lors de la télétransmission.' });
        },
      });
  }

  /**
   * Standalone/granular teletransmission execution (from the actions menu).
   */
  onClickTeletransmissionSolo(): void {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    this.fseService
      .teletransmettre$(area_baseurl)
      .pipe(switchMap(() => this.loadFses()))
      .subscribe({
        next: () => {
          console.log('teletransmettre solo process completed successfully.');
        },
        error: (error: any) => {
          console.error('Error in teletransmettre solo:', error);
          Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || 'Une erreur est survenue lors de la télétransmission.' });
        },
      });
  }

  onClickDetailPrestation(_event: MouseEvent, fSEOrder: fselib.FSEOrder) {
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    //console.log("p: " + JSON.stringify(fSEOrder, null, 2));
    if (fSEOrder.etat_facture === 'NOUVELLE FSE' || fSEOrder.etat_facture === 'SECURISEE') return;
    //console.log(`[onClickDetailPrestation] ${JSON.stringify(this.numIdentFact, null, 2)}`);
    const dialogRef = this.dialog.open(DetailComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        fSEOrder: fSEOrder,
        area_baseurl: area_baseurl,
        numIdentFact: this.numIdentFact,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      console.log('The detail dialog was closed');
    });
  }

  onClickDetailLot(event: MouseEvent, fSEOrder: fselib.FSEOrder, mode: string) {
    // mode: 'FSE' pour RSP FSE / 'DRE' pour RSP DRE
    //console.log("p: " + JSON.stringify(fSEOrder, null, 2));
    event.stopPropagation();
    const dialogRef = this.dialog.open(DetailLotComponent, {
      width: 'auto', // Adjusts to content width
      maxWidth: '95vw', // Prevents dialog from hitting screen edges
      //height: '900px',
      panelClass: 'no-maxheight-dialog',
      //maxHeight: 'none',
      //maxWidth: 'none',
      data: {
        fSEOrder: fSEOrder,
        mode: mode,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      console.log('The detail dialog was closed');
    });
  }

  // WARNING: Only use for 'Nouvelle FSE' or 'Securisee' if the FSE has not been transmitted to the CNAM
  // This is NOT guaranted by the API server
  onClickDeleteOrder(fSEOrder: fselib.FSEOrder): void {
    if (fSEOrder.etat_facture === 'MODELE_LOCATION') {
      Swal.fire({
        icon: 'warning',
        title: 'Action impossible',
        text: 'Une facture MODELE_LOCATION ne peut pas être supprimée.',
      });
      return;
    }

    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    let dateFacture = tofseformat(fSEOrder.data.factureFR.dateFacture);

    const numIdentFact = fSEOrder.data?.factureFR?.PS?.numIdentFact || this.numIdentFact || this.user?.data?.FSE?.PS?.numIdentFact || this.loginService.loggeduser?.data?.FSE?.PS?.numIdentFact;

    if (!numIdentFact) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Identifiant de facturation (numIdentFact) introuvable pour cette FSE.' });
      return;
    }

    // Fetch local fse_docs, delete from Area if needed, then delete local record.
    // Finally, delete FSE from Area (if SECURISEE), and delete FSE locally.
    this.apiService.getDocsByFseId$(environment.server_url, fSEOrder.id).pipe(
      switchMap((docs: any[]) => {
        if (!docs || docs.length === 0) return of(null);

        return from(docs).pipe(
          concatMap((doc: any) => {
            let deleteArea$ = of(null);
            // Delete from Area only if it has an idpj
            if (doc.idpj) {
              deleteArea$ = this.meklibService.supprimerPJ$(area_baseurl, numIdentFact, fSEOrder.data.factureFR.numFacture, dateFacture, doc.idpj).pipe(
                switchMap((res: any) => {
                  if (+res.HTTPResponse.codeRetour < 0) {
                    return throwError(() => new Error(`Area Erreur suppression PJ: ${res.HTTPResponse.libRetour}`));
                  }
                  return of(null);
                })
              );
            }
            return deleteArea$.pipe(
              switchMap(() => this.apiService.postDocDeleteEvent$(environment.server_url, doc))
            );
          }),
          toArray()
        );
      }),
      switchMap(() => {
        if (fSEOrder.etat_facture == 'SECURISÉE') {
          return this.meklibService.supprimerFSE$(area_baseurl, numIdentFact, fSEOrder.data.factureFR.numFacture, dateFacture).pipe(
            switchMap((supprimerHTTPResponse: SupprimerHTTPResponse) => {
              if (+supprimerHTTPResponse.HTTPResponse.codeRetour < 0) {
                return throwError(() => new Error(`Erreur Area FSE: ${supprimerHTTPResponse.HTTPResponse.libRetour}`));
              }
              return of(null);
            })
          );
        }
        return of(null);
      }),
      switchMap(() => this.fseService.postFSEDeleteEvent$(environment.server_url, fSEOrder))
    ).subscribe({
      next: () => {
        console.log('FSE et documents supprimés: ', fSEOrder.data.factureFR.numFacture);
        this.alert.success('FSE supprimée');
        this.loadFses().subscribe();
      },
      error: (error: any) => {
        console.error('Erreur à la suppression de FSE: ', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: error.message || 'Une erreur est survenue lors de la suppression de la FSE.' });
      }
    });
  }

  onClickSearch() {
    this.loadFses().subscribe();
  }

  onIDOrderClick(event: Event) {
    //event.preventDefault();
    event.stopPropagation();
  }

  onClickClear() {
    this.filters = {
      datdeb: '',
      datfin: '',
      numFacture: '',
      recipient: '',
      etats: [],
      orgAmo: '',
      datcpt: '',
      paiement: '',
    };
    this.loadFses().subscribe();
  }

  opendossiersscore(event: Event, fseorder: fselib.FSEOrder, attachMode: boolean = false) {
    event.stopPropagation();
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    const dialogRef = this.dialog.open(DossiersScoreComponent, {
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: environment.server_url,
        area_baseurl: area_baseurl,
        fseOrder: fseorder, // Pass the entire FSEOrder object
        userid: this.loginService.loggeduser?.id,
        outputDialogData: { empreinteCV: fseorder.data.empreinteCV, index: fseorder.data.index, numIdentFact: this.numIdentFact },
        attachMode: attachMode,
        station: this.loginService.currentstation,
        configarea: this.configarea,
      },
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  onClickFSE(event: Event, fseorder: fselib.FSEOrder, attachMode: boolean = false) {
    if (fseorder.data.processingResult?.areaResponse?.HTTPResponse.codeRetour != '-1401') {
      // Normal case, Open FSE (bsDialog)
      this.openbsdialog(event, fseorder, attachMode);
    } else {
      // Case -1401, reopen recipients, then openbsdialog
      const dialogRef = this.dialog.open(RecipientsComponent, {
        // Choose recipient from HC Card
        width: '533px',
        height: '400px',
        maxWidth: 'unset',
        data: { area_baseurl: this.area_baseurl, server_url: environment.server_url, userid: this.loginService.loggeduser?.id, numIdentFact: this.numIdentFact, isTokenExpired: true },
      });
      this.recipientssubscription = dialogRef.afterClosed().subscribe((outputDialogData: OutputDialogData) => {
        // Recipient chosen => open partner selector to match patient w/ customer
        if (outputDialogData == undefined) {
          // Escape
          return;
        }
        if (outputDialogData !== undefined) {
          // Normal case, we have data to get a FSE
          console.log('[openRecipientsDialog] opening bs dialog');
          this.openbsdialog(event, fseorder, attachMode); // Now jump to BS page
        }
      });
    }
  }

  openbsdialog(event: Event, fseorder: fselib.FSEOrder, attachMode: boolean = false) {
    // Open an EXISTING FSE
    console.log(`[openbsdialog] fseorder : ${JSON.stringify(fseorder.data, null, 2)}`);
    console.log(`[openbsdialog] codeRetour : ${fseorder.data.processingResult?.areaResponse?.HTTPResponse.codeRetour}`);
    event.stopPropagation();
    const area_baseurl = this.area_baseurl;
    if (!area_baseurl) return;
    const dialogRef = this.dialog.open(fselib.BsDialogComponent, {
      //width: '1280px',
      //height: '900px',
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: environment.server_url,
        area_baseurl: area_baseurl,
        order_id: fseorder.id_order,
        fse_id: fseorder.id, // Means existing
        userid: this.loginService.loggeduser?.id,
        outputDialogData: { mode: fseorder.data.mode, empreinteCV: fseorder.data.empreinteCV, empreinteADR: fseorder.data.empreinteADR, contexteAuthentificationApCV: fseorder.data.contexteAuthentificationApCV, index: fseorder.data.index, numIdentFact: this.numIdentFact },
        attachMode: attachMode,
        station: this.loginService.currentstation,
        configarea: this.configarea,
      },
    });
    this.openbssubscription = dialogRef.afterClosed().subscribe(() => {
      //console.log('The BS dialog was closed');
    });
  }

  getAttachmentIcon(fseorder: fselib.FSEOrder): { icon: string; color: string } {
    let icon: string = 'attach_file'; // Default icon
    let color: string = 'black';
    let ischeck: boolean | undefined = undefined;
    let isinprogress: boolean = false;
    let isnew: boolean | undefined = undefined;
    let istoprint: boolean | undefined = undefined;
    if (Array.isArray(fseorder.lnk_docs)) {
      fseorder.lnk_docs.forEach((doc: fselib.FseDoc) => {
        ischeck = (ischeck == undefined ? doc.codeaction == 'RECU_AMO' : ischeck) && doc.codeaction == 'RECU_AMO'; // No check if anyone is NOT RECU_AMO
        isinprogress = isinprogress || doc.codeaction == 'EN_COURS'; // In progress if someone is in progress
        isnew = (isnew == undefined ? doc.codeaction == undefined : isnew) && doc.codeaction == undefined; // New if everyone is new
        istoprint = istoprint || doc.codeaction == 'A_IMPRIMER'; // Must print if anyone is to be printed
      });
    }
    if (istoprint) {
      icon = 'warning';
      color = 'red';
    } // Red
    else if (isinprogress) {
      icon = 'hourglass_bottom';
      color = 'dimgrey';
    } // dark grey
    else if (ischeck) {
      icon = 'check';
      color = 'green';
    } // Green
    else if (isnew) {
      icon = 'star';
      color = 'dimgrey';
    } // light Grey

    return { icon, color };
  }

  // Pre computation of FSEOrder.etatFactureRSP. This is the state AFTER RSP integration
  getEtatFactureRSP(fo: fselib.FSEOrder): [FSE: fselib.EtatFactureRSP, AMO: fselib.EtatFactureRSP, AMC: fselib.EtatFactureRSP] {
    // "Nouvelle" | "Sécurisée" | "Payée" | "Rejeté" | "Différé" | "en Cours" | "Payé (forçage)"

    function getAMxStatus(rspFses: fselib.RSP[], rspDres: fselib.RSP[], typeFlux: string): { amo: fselib.EtatFactureRSP; amc: fselib.EtatFactureRSP } {
      // Payé, Rejeté, Différé, En cours (en attente), No part
      let amoStatus: fselib.EtatFactureRSP | undefined = 'C';
      let amcStatus: fselib.EtatFactureRSP | undefined = typeFlux == 'FSE_SEULE' ? 'NO' : 'C';

      //console.log(`typeFlux ${typeFlux} amcStatus: ${amcStatus}`);

      for (let i = 0; i < rspFses.length; i++) {
        // Loop over RSP FSE to compute amoStatus and potentially amcStatus (GU)
        const rspFse: fselib.RSP = rspFses[i];

        // if (rspFse.MFI) break;	// Ignore MFI RSPs

        if (rspFse.norme == '580') {
          // RSP AMO en norme 580 => status is in partAMO
          if (rspFse.partAMO && fselib.isEtatFactureRSP(rspFse.partAMO.traitement)) {
            // There is a partAmo to process
            if (amoStatus !== 'A') amoStatus = rspFse.partAMO.traitement as fselib.EtatFactureRSP; // A is a final state. Do not change
            if (amoStatus !== 'D' && amoStatus !== 'R' && rspFse.partAMO && rspFse.partAMO.montantDemande !== rspFse.partAMO.montantPaiement) amoStatus = 'A'; // Cas Anomalie (cas particulier D est prioritaire sur A)
          }
        } else if (['576', '900'].includes(rspFse.norme)) {
          // CTT 12.62.02 status is RSP global status. Several tops can be active, order is important
          if (rspFse.topDiffere === '1') amoStatus = 'D'; // Différé
          if (rspFse.topRejet === '1') amoStatus = 'R'; // Rejeté
          const totalRemboursableAmo = fo.data?.processingResult?.areaResponse?.HTTPResponse?.factureFR?.totalFacture?.totalRemboursableAmo;
          if (rspFse?.totalAMO && rspFse.topPaiement === '1' && rspFse?.totalAMO <= totalRemboursableAmo) {
            amoStatus = 'P';
          }
        }

        // TODO It is unclear what to do on no 580
        //else // No partAMO (no 580) => status is RSP global status. Several tops can be active, order is important
        /*if (amoStatus !== "A") {							
          if (rspFse.topPaiement === '1') amoStatus = "P";   // Payé
          if (rspFse.topDiffere === '1') amoStatus = "D";    // Différé
          if (rspFse.topRejet === '1') amoStatus = "R";      // Rejeté
        }*/
        if (!amoStatus) amoStatus = 'C'; // Defaults to "En cours"

        //console.log(`  Step ${i+1} norme / amcstatus / montantDemande / montantPaiement ${rspFse.norme} / ${rspFse?.partAMC?.montantDemande} / ${rspFse?.partAMC?.montantPaiement}`);
        if (rspFse.norme == '580') {
          // RSP FSE en norme 580
          if (rspFse.partAMC && fselib.isEtatFactureRSP(rspFse.partAMC.traitement)) {
            // GU => partAMC => amc status is in partAMC
            console.log(`    - Step ${i + 1} RSP FSE en norme 580 en GU`);
            if (amcStatus !== 'A') amcStatus = rspFse.partAMC.traitement as fselib.EtatFactureRSP; // A is a final state. Do not change
            if (amcStatus !== 'D' && amcStatus !== 'R' && rspFse.partAMC && rspFse.partAMC.montantDemande !== rspFse.partAMC.montantPaiement) amcStatus = 'A'; // Cas Anomalie seulement si non D et non R
          }
        }
        /* TODO detection cas pas de status AMC
        if ((!rspDres || rspDres.length === 0) && typeFlux === 'FSE_SEULE') {										// 'FSE_Seule' AND no rsp DRE => amc status == NO
          amcStatus = "NO";
        } 
        */
        if (rspDres) {
          // RSPs DRE got the status. Loop over them
          for (let i = 0; i < rspDres.length; i++) {
            // Loop over RSP FSE to compute amoStatus and potentially amcStatus (GU)
            const rspDre: fselib.RSP = rspDres[i];
            if (rspFse.norme == '580') {
              // RSP AMC en norme 580 => status is in partAMC
              if (rspDre.partAMC && fselib.isEtatFactureRSP(rspDre.partAMC.traitement)) {
                if (amcStatus !== 'A') amcStatus = rspDre.partAMC.traitement as fselib.EtatFactureRSP; // A is a final state. Do not change
                if (amcStatus !== 'D' && amcStatus !== 'R' && rspDre.partAMC && rspDre.partAMC.montantDemande !== rspDre.partAMC.montantPaiement) amcStatus = 'A'; // Cas Anomalie
              }
            } // TODO It is unclear what to do on no 580
            /*else 
            if (amcStatus !== "P") {							// No partAMO (no 580) => status is RSP global status. Several tops can be active, order is important
              if (rspDre.topPaiement === '1') amoStatus = "P";   // Payé
              if (rspDre.topDiffere === '1') amoStatus = "D";    // Différé
              if (rspDre.topRejet === '1') amoStatus = "R";      // Rejeté
            }*/
            if (!amcStatus) amcStatus = 'C'; // Defaults to "En cours"
          }
        }

        console.log(`  Step ${i + 1} amoStatus / amcStatus: ${amoStatus}/${amcStatus}`);
      }
      if (!amcStatus) amcStatus = 'C'; // Defaults to "En cours"
      return { amo: amoStatus, amc: amcStatus };
    }

    /* Combine amo & amc status to compute fseStatus */

    // Special case, no need to compute amo & amc. Not in AF yet
    if (fo.etat_facture === 'NOUVELLE FSE') return ['N', 'C', 'C']; // FSE Not yet in AF
    if (fo.etat_facture === 'SECURISÉE') return ['S', 'C', 'C']; // FSE Not yet in AF
    if (fo.etat_facture === 'PAYÉ_FORÇAGE') return ['PF', 'C', 'C']; // RSP forcé à PAYÉ_FORÇAGE

    //return ['N'as fselib.EtatFactureRSP, 'N'as fselib.EtatFactureRSP, 'N'as fselib.EtatFactureRSP];

    const rspFses = fo.lnk_rsp_fses ?? [];
    const rspDres = fo.lnk_rsp_dres ?? [];

    const amxStatus = getAMxStatus(rspFses, rspDres, fo.data?.processingResult?.areaResponse?.HTTPResponse?.factureFR?.typeFluxCree);
    const amoStatus = amxStatus.amo;
    const amcStatus = amxStatus.amc;

    let fseStatus: fselib.EtatFactureRSP = 'C'; // Resulted FSE status. Need to be adjusted w/ amo and amc:

    if (amcStatus === 'NO') {
      // No AMC => return AMC status
      fseStatus = amoStatus;
    }

    if (amoStatus === 'P' && amcStatus === 'P') {
      // 2 payés => payé. Le cas P/N est traité au-dessus
      fseStatus = 'P';
    }

    if ((amoStatus === 'P' && amcStatus === 'R') || (amoStatus === 'R' && amcStatus === 'P') || (amoStatus === 'R' && amcStatus === 'R')) {
      // One is rejected AND the other one is payed OR rejected => R
      fseStatus = 'R';
    }

    if (amoStatus === 'A' || amcStatus === 'A') {
      // 2 Anomalies => Anomalie. Le cas P/N est traité au-dessus
      fseStatus = 'A';
    }
    //console.log(`\n id:${fo.id} Final states fseStatus / amoStatus / amcStatus: ${fseStatus} / ${amoStatus} / ${amcStatus}`);
    return [fseStatus, amoStatus, amcStatus];
  }

  onKeydown(event: any) {
    // TEMPLATE event.key === "Enter"
    if (event.key === 'Enter') {
      this.onClickSearch();
    }
  }

  ngOnDestroy() {
    this.routerevents$.unsubscribe(); // This prevents an event triggering on futur page loading
  }
}
